import React, { useState } from 'react';
import { ArrowLeft, Layers, Edit2, X } from 'lucide-react';
import styles from './ContentDetail.module.css';
import ScrollToTop from '../ScrollToTop/ScrollToTop';
import PptxPreview from '../PptxPreview/PptxPreview';
import MarkdownEditor from '../MarkdownEditor/MarkdownEditor';
import { authenticatedFetch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useDocumentHead } from '../../hooks/useDocumentHead';

// Custom wrapper component to handle image loading
const ImageWithLoading = ({ src, alt, className }) => {
    const [isLoading, setIsLoading] = useState(true);

    return (
        <div className={styles.imageWrapper}>
            {isLoading && (
                <div className={styles.imageLoadingPlaceholder}>
                    <div className={styles.loadingSpinner}></div>
                </div>
            )}
            <img
                src={src}
                alt={alt}
                className={`${className} ${!isLoading ? styles.loaded : ''}`}
                onLoad={() => setIsLoading(false)}
            />
        </div>
    );
};

// Custom wrapper to prevent invalid nesting
const CustomParagraph = ({ children }) => {
    // Check if children contain block-level elements
    const hasBlockElements = React.Children.toArray(children).some(child =>
        React.isValidElement(child) &&
        ['div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'ul', 'ol'].includes(child.type)
    );

    // If there are block elements, render a div instead of a p
    return hasBlockElements ? <div className={styles.paragraphWrapper}>{children}</div> : <p>{children}</p>;
};

const ContentDetail = ({ content, onBack }) => {
    const [showPptxPreview, setShowPptxPreview] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedContent, setEditedContent] = useState(content.content);
    const [isSaving, setIsSaving] = useState(false);
    const { user } = useAuth();

    const slideCount = content.content.split(/(?=## )/).filter(section => section.trim()).length;
    const isOwner = user?.id === content.created_by;

    useDocumentHead(
        `${content.title} by ${content.author} | TopSpeaks AI Presentations`,
        `View and edit AI-generated presentation: ${content.title}. Create professional presentations instantly with TopSpeaks' AI-powered presentation generator.`,
        'index, follow'
    );
    // Custom components for markdown rendering
    const components = {
        img: ({ node, ...props }) => <ImageWithLoading {...props} />,
        p: CustomParagraph
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await authenticatedFetch(`/api/ai-presentations/${content._id}`, {
                method: 'PUT',
                body: {
                    content: editedContent
                }
            });
            setIsEditing(false);
        } catch (error) {
            alert('Failed to save changes: ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setEditedContent(content.content);
        setIsEditing(false);
    };

    return (
        <>
            <ScrollToTop />
            <div className={styles.container}>
                <button className={styles.backButton} onClick={onBack}>
                    <ArrowLeft size={20} />
                    Back to Presentations
                </button>

                <div className={styles.content}>
                    <div className={styles.header}>
                        <div className={styles.presentationMeta}>
                            <div className={styles.slideCount}>
                                <Layers size={16} />
                                {slideCount} slides
                            </div>
                            <div className={styles.actionButtons}>
                                {!showPptxPreview && isOwner && (
                                    <button
                                        className={`${styles.editButton} ${isEditing ? styles.cancelButton : ''}`}
                                        onClick={isEditing ? handleCancel : () => setIsEditing(true)}
                                    >
                                        {isEditing ? (
                                            <>
                                                <X size={15} />
                                                Cancel
                                            </>
                                        ) : (
                                            <>
                                                <Edit2 size={15} />
                                                Edit Content
                                            </>
                                        )}
                                    </button>
                                )}
                                <button
                                    className={`${styles.viewToggle} ${isEditing ? styles.saveButton : ''}`}
                                    onClick={isEditing ? handleSave : () => setShowPptxPreview(!showPptxPreview)}
                                    disabled={isSaving}
                                >
                                    {isEditing ? (
                                        <>
                                            {isSaving ? 'Saving Changes...' : 'Save Changes'}
                                        </>
                                    ) : (
                                        showPptxPreview ? 'View Content' : 'Preview Slides'
                                    )}
                                </button>
                            </div>
                        </div>

                        <h1 className={styles.title}>{content.title}</h1>

                        <div className={styles.metadata}>
                            <div className={styles.metadataItem}>
                                <span className={styles.label}>Created by</span>
                                {content.author}
                            </div>
                            <div className={styles.metadataItem}>
                                <span className={styles.label}>Generated on</span>
                                {new Date(content.date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </div>
                        </div>
                    </div>

                    <div className={styles.mainContent}>
                        {showPptxPreview ? (
                            <PptxPreview
                                markdown={content.content}
                                title={content.title}
                                author={content.author}
                            />
                        ) : (
                            <div className={styles.markdown}>
                                <MarkdownEditor
                                    content={editedContent}
                                    onContentChange={setEditedContent}
                                    components={components}
                                    isEditing={isEditing}
                                    placeholder="Write your content here..."
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContentDetail;